import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAppContext } from '@/contexts/AppContext';
import { useTranslation } from '@/contexts/TranslationContext';
import { toast } from '@/components/ui/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { Globe } from 'lucide-react';

interface LoginFormProps {
  onLogin: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, loading } = useAppContext();
  const { t, language, setLanguage } = useTranslation();
  const isMobile = useIsMobile();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      const errorMsg = language === 'el' 
        ? 'Παρακαλώ εισάγετε email και κωδικό πρόσβασης'
        : 'Please enter email and password';
      toast({ title: t('error'), description: errorMsg, variant: 'destructive' });
      return;
    }
    
    const success = await login(email, password);
    if (success) {
      onLogin();
    }
  };

  const fillDemoCredentials = (userType: string) => {
    if (userType === 'admin') {
      setEmail('admin@winery.com');
      setPassword('admin123');
    } else {
      setEmail('john@winery.com');
      setPassword('john123');
    }
  };

  const getLocalizedText = (enText: string, elText: string) => {
    return language === 'el' ? elText : enText;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <Card className={`w-full ${isMobile ? 'max-w-sm' : 'max-w-md'}`}>
        <CardHeader className={isMobile ? 'pb-4' : ''}>
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              <CardTitle className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>
                Winery CRM
              </CardTitle>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="h-4 w-4 text-gray-500" />
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">EN</SelectItem>
                  <SelectItem value="el">ΕΛ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <CardDescription className={`text-center ${isMobile ? 'text-sm' : ''}`}>
            {getLocalizedText('Sign in to your account', 'Συνδεθείτε στον λογαριασμό σας')}
          </CardDescription>
        </CardHeader>
        <CardContent className={isMobile ? 'pt-0' : ''}>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className={isMobile ? 'text-sm' : ''}>
                {t('email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={getLocalizedText(`Enter your ${t('email').toLowerCase()}`, `Εισάγετε το ${t('email').toLowerCase()}`)}
                required
                className={isMobile ? 'text-base h-12' : ''}
                autoComplete="email"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className={isMobile ? 'text-sm' : ''}>
                {t('password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={getLocalizedText(`Enter your ${t('password').toLowerCase()}`, `Εισάγετε τον ${t('password').toLowerCase()}`)}
                required
                className={isMobile ? 'text-base h-12' : ''}
                autoComplete="current-password"
              />
            </div>
            
            <Button 
              type="submit" 
              className={`w-full ${isMobile ? 'h-12 text-base' : ''}`} 
              disabled={loading}
            >
              {loading ? t('loading') : t('login.button')}
            </Button>
          </form>
          
          <div className={`${isMobile ? 'mt-4 pt-4' : 'mt-6 pt-6'} border-t`}>
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 mb-2`}>
              {getLocalizedText('Demo accounts:', 'Δοκιμαστικοί λογαριασμοί:')}
            </p>
            <div className={`flex gap-2 ${isMobile ? 'flex-col' : ''}`}>
              <Button 
                variant="outline" 
                size={isMobile ? 'default' : 'sm'}
                onClick={() => fillDemoCredentials('admin')}
                className={`flex-1 ${isMobile ? 'h-10 text-sm' : ''}`}
              >
                {getLocalizedText('Admin', 'Διαχειριστής')}
              </Button>
              <Button 
                variant="outline" 
                size={isMobile ? 'default' : 'sm'}
                onClick={() => fillDemoCredentials('sales')}
                className={`flex-1 ${isMobile ? 'h-10 text-sm' : ''}`}
              >
                {getLocalizedText('Sales', 'Πωλήσεις')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginForm;